import type { CoreLocationData } from "./prepareOfferDataCore";
import { shouldForceHideFromWorkflowStatus } from "../workflowForceHide";

/**
 * KB-4619 — the ONE rule for an offer's `locationData` stamp, shared by `prepareOfferDataCore` (every
 * admin / web / server offer write) and the debounced location restamp (`offers/offerLocationRestamp.ts`),
 * so a restamp can never write a value a fresh publish would not.
 *
 * The pipeline's `.cjs` twin (`scripts/camp-data/_reconcile/offerComputeCore.cjs`) builds the same
 * literal; `offerLocationData.parity.test.ts` pins the two together.
 *
 * Hero rule: raw `photos[0]`, as every writer has always stamped. Marketing's camp page sorts by `order`
 * instead; the two disagree only where the array is out of `order` sequence (KB-4619 census: 6 of 424).
 */
export function buildOfferLocationData(location: CoreLocationData): Record<string, unknown> {
  const photo: { path?: string; yOffset?: number } =
    Array.isArray(location.photos) && location.photos.length > 0 ? location.photos[0] : {};

  const nameSubtitle = typeof location.nameSubtitle === "string" ? location.nameSubtitle.trim() : "";

  return {
    address: location.address || {},
    allergyKeys: (location as any).allergyKeys || [],
    coedKey: location.coedKey || "",
    descAboutTheOrg: location.descAboutTheOrg || "",
    descLocation: location.descLocation || "",
    descTimingAndBooking: location.descTimingAndBooking || "",
    doesScholarships: location.doesScholarships ?? false,
    doesWaitlist: location.doesWaitlist ?? false,
    hasReligiousAffiliationTag: Array.isArray(location.religiousAffiliationKeys) && location.religiousAffiliationKeys.length > 0 && !(location.religiousAffiliationKeys as any).includes('r_none') || false,
    name: location.name || location.id,
    ...(nameSubtitle ? { nameSubtitle } : {}),
    // IMPORTANT: orgName is the campOrganization name (NOT the location name).
    // Do not silently fall back to location.name; that masks data issues and writes wrong values.
    orgName: location.orgName || "",
    photoPath: photo.path || "",
    photoYOffset: photo.yOffset ?? 0,
    providedByOrg: location.providedByOrg || null,
    religiousAffiliationKeys: location.religiousAffiliationKeys || [],
    supportForKeys: location.supportForKeys || [],
    searchResultExcerpt: location.searchResultExcerpt || "",
    visibility: location.visibility || "draft",
    forceHide: Boolean((location as any).forceHide)
  };
}

/**
 * Every key `buildOfferLocationData` can emit. `nameSubtitle` is conditional, so a restamp must be able
 * to DELETE it; nothing outside this list (notably `slugPath`, owned by `syncCampSlugPaths`) is ever
 * touched by a restamp.
 */
export const OFFER_LOCATION_DATA_KEYS = [
  "address",
  "allergyKeys",
  "coedKey",
  "descAboutTheOrg",
  "descLocation",
  "descTimingAndBooking",
  "doesScholarships",
  "doesWaitlist",
  "hasReligiousAffiliationTag",
  "name",
  "nameSubtitle",
  "orgName",
  "photoPath",
  "photoYOffset",
  "providedByOrg",
  "religiousAffiliationKeys",
  "supportForKeys",
  "searchResultExcerpt",
  "visibility",
  "forceHide",
] as const;

/**
 * The offer's `forceHide`: derived from `workflowStatus` when the location has one, else the raw flag.
 * Raw `campLocations.forceHide` is NOT the rule — archived locations routinely carry `forceHide: false`
 * (KB-4619 census: 21 of 424 disagree), and stamping the raw flag would un-hide them.
 */
export function offerForceHideForLocation(locationDoc: Record<string, any>): boolean {
  const wfRaw = locationDoc.workflowStatus;
  const wf = typeof wfRaw === "string" && wfRaw !== "" ? wfRaw : null;
  return wf != null ? shouldForceHideFromWorkflowStatus(wf) : locationDoc.forceHide === true;
}

/** A location's own legacy `orgName`, the fallback when the campOrganizations lookup yields nothing. */
export function legacyOrgNameOf(locationDoc: Record<string, any>): string {
  const legacy = locationDoc?.orgName;
  return typeof legacy === "string" ? legacy : "";
}

/** Maps a `campLocations` doc to the `location` input `prepareOfferDataCore` takes. */
export function offerLocationCoreInput(
  locationId: string,
  locationDoc: Record<string, any>,
  resolvedOrgName: string,
): CoreLocationData {
  return {
    id: locationId,
    name: locationDoc.name || locationId,
    // Match web `prepareOfferData`: pass through raw subtitle; core trims / omits empty.
    nameSubtitle: locationDoc.nameSubtitle,
    address: locationDoc.address,
    orgName: resolvedOrgName,
    searchResultExcerpt: locationDoc.searchResultExcerpt,
    photos: locationDoc.photos,
    campExperiences: locationDoc.campExperiences,
    providedByOrg: locationDoc.providedByOrg,
    religiousAffiliationKeys: locationDoc.religiousAffiliationKeys,
    allergyKeys: locationDoc.allergyKeys,
    visibility: locationDoc.visibility,
    forceHide: offerForceHideForLocation(locationDoc),
    doesScholarships: locationDoc.doesScholarships,
    doesWaitlist: locationDoc.doesWaitlist,
    coedKey: locationDoc.coedKey,
    descAboutTheOrg: locationDoc.descAboutTheOrg,
    descLocation: locationDoc.descLocation,
    descTimingAndBooking: locationDoc.descTimingAndBooking,
    supportForKeys: locationDoc.supportForKeys
  };
}
